import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useCollection, where, orderBy } from '../../lib/hooks.js';
import { formatCents, formatTimestamp } from '../../lib/format.js';
import type { BillStatus } from '@sfp/shared/schemas';

interface BillRow {
  id: string;
  biller?: { name?: string; category?: string };
  amountDue?: number;
  dueDate?: { seconds: number };
  status?: BillStatus;
  payingEntityId?: string;
  paidVia?: string;
}

const STATUS_TABS: Array<{ key: BillStatus | 'all' | 'open'; label: string }> = [
  { key: 'open', label: 'Open (due soon)' },
  { key: 'received', label: 'New' },
  { key: 'approved', label: 'Approved' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

export function BillsList() {
  const [tab, setTab] = useState<(typeof STATUS_TABS)[number]['key']>('open');

  const constraints = (() => {
    switch (tab) {
      case 'all':
        return [orderBy('dueDate', 'asc')];
      case 'open':
        // "Open" = anything not paid/ignored. Firestore can't do "not in", so
        // we query received+review+approved+scheduled and merge — for v1,
        // approximate with received+review+approved sorted by dueDate.
        return [where('status', 'in', ['received', 'review', 'approved']), orderBy('dueDate', 'asc')];
      default:
        return [where('status', '==', tab), orderBy('dueDate', 'asc')];
    }
  })();

  const { docs, loading } = useCollection<BillRow>('bills', constraints);

  const total = docs.reduce((s, b) => s + (b.amountDue ?? 0), 0);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bills</h1>
          <p className="text-sm text-stone-500">
            {docs.length} bills · {formatCents(total)} total
          </p>
        </div>
      </header>

      <nav className="flex gap-1.5 border-b border-stone-200">
        {STATUS_TABS.map((t) => (
          <button
            type="button"
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-stone-900 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">
            No bills in this view.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-4 py-2">Biller</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Due</th>
                <th className="px-4 py-2">Paying entity</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((b) => (
                <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-2">
                    <Link to={`/bills/${b.id}`} className="font-medium text-stone-900">
                      {b.biller?.name ?? 'Unknown'}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="chip">{b.biller?.category ?? '—'}</span>
                  </td>
                  <td className="px-4 py-2 font-mono">{formatCents(b.amountDue)}</td>
                  <td className="px-4 py-2 text-xs">{formatTimestamp(b.dueDate)}</td>
                  <td className="px-4 py-2 text-xs text-stone-600">
                    {b.payingEntityId ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <BillStatusChip status={b.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BillStatusChip({ status }: { status: BillStatus | undefined }) {
  if (!status) return <span className="chip">—</span>;
  if (status === 'paid') return <span className="chip-success">Paid</span>;
  if (status === 'received') return <span className="chip">New</span>;
  if (status === 'review') return <span className="chip-warn">In review</span>;
  if (status === 'approved') return <span className="chip-warn">Approved</span>;
  if (status === 'disputed') return <span className="chip-error">Disputed</span>;
  if (status === 'ignored') return <span className="chip">Ignored</span>;
  return <span className="chip">{status}</span>;
}
